import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumber } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { AuthService } from '@brightside/core/auth';
import type {
  FinancialSnapshotDocument,
  FinancialSnapshotResponse,
  FinancialSnapshotTemplate,
  SnapshotCategoryData,
} from '../financial-snapshot.model';
import {
  countEnteredUiRows,
  FINANCIAL_SNAPSHOT_UI_ROW_COUNT,
  recomputeSnapshot,
} from '../mock/financial-snapshot-compute';
import { FinancialSnapshotStore } from '../financial-snapshot.store';
import { FinancialSnapshotAnalytics } from '../analytics/financial-snapshot-analytics';
import {
  buildCategoriesFromForm,
  buildDocumentFromForm,
  categorySubKey,
  categorySumFromForm,
  changedCategoryKeys,
  formValuesEqual,
  initFormValues,
  initOtherCustoms,
  incomeCategoryKey,
  newOtherCustomKey,
  OTHER_CUSTOM_LABEL_MAX_LEN,
  otherCustomsEqual,
  otherSubKey,
  sanitizeOtherCustomLabel,
  SAVINGS_KEY,
  spendingCategoryKeys,
  stampFaAudit,
  type OtherCustomEntry,
  type SnapshotFormValues,
} from './financial-snapshot-edit.model';

export interface FinancialSnapshotEditDialogData {
  guid: string;
  snapshot: FinancialSnapshotResponse | null;
  template: FinancialSnapshotTemplate;
  store: FinancialSnapshotStore;
}

@Component({
  selector: 'bn-financial-snapshot-edit-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CurrencyPipe,
    Button,
    InputNumber,
    InputTextModule,
    ConfirmDialog,
    Tooltip,
  ],
  providers: [ConfirmationService],
  templateUrl: './financial-snapshot-edit-dialog.component.html',
  styleUrl: './financial-snapshot-edit-dialog.component.scss',
})
export class FinancialSnapshotEditDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _messages = inject(MessageService);
  private readonly _auth = inject(AuthService);
  private readonly _analytics = inject(FinancialSnapshotAnalytics);

  private readonly _data = this._config.data as FinancialSnapshotEditDialogData;

  readonly guid = this._data.guid;
  readonly template = this._data.template;
  readonly store = this._data.store;

  readonly formValues = signal<SnapshotFormValues>(
    initFormValues(this._data.snapshot, this.template)
  );
  /** Diff baseline — rebased from server after a partial PATCH failure. */
  private readonly _baselineSnapshot = signal<FinancialSnapshotResponse | null>(this._data.snapshot);
  private readonly _initialValues = signal<SnapshotFormValues>(
    initFormValues(this._data.snapshot, this.template)
  );
  readonly otherCustoms = signal<OtherCustomEntry[]>(initOtherCustoms(this._data.snapshot));
  private readonly _initialCustoms = signal<OtherCustomEntry[]>(
    initOtherCustoms(this._data.snapshot)
  );

  readonly expanded = signal<Record<string, boolean>>({
    [incomeCategoryKey(this.template) ?? 'monthlyhouseholdincome']: true,
  });

  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);

  readonly isSubmitted = !!this._data.snapshot?.submittedDate;

  readonly incomeKey = incomeCategoryKey(this.template) ?? 'monthlyhouseholdincome';
  readonly spendingKeys = spendingCategoryKeys(this.template);
  readonly otherKey = 'other';
  readonly otherNamed = [
    {
      key: 'activities',
      label: this.template['other']?.subCategories?.['activities']?.label ?? 'Activities',
      shortLabel: 'Activities',
    },
    {
      key: 'medical',
      label: this.template['other']?.subCategories?.['medical']?.label ?? 'Medical',
      shortLabel: 'Medical',
    },
    {
      key: 'personal',
      label: this.template['other']?.subCategories?.['personal']?.label ?? 'Personal',
      shortLabel: 'Personal',
    },
  ] as const;
  readonly savingsFormKey = SAVINGS_KEY;
  readonly otherCustomLabelMaxLen = OTHER_CUSTOM_LABEL_MAX_LEN;

  readonly preview = computed(() => {
    const doc = buildDocumentFromForm(
      this.guid,
      this.formValues(),
      this.template,
      this._baselineSnapshot(),
      this.otherCustoms()
    );
    return recomputeSnapshot(doc, this.template);
  });

  readonly enteredCount = computed(() =>
    countEnteredUiRows(
      buildDocumentFromForm(
        this.guid,
        this.formValues(),
        this.template,
        this._baselineSnapshot(),
        this.otherCustoms()
      ),
      this.template
    )
  );

  readonly canSubmit = computed(
    () => !this.isSubmitted && this.enteredCount() >= FINANCIAL_SNAPSHOT_UI_ROW_COUNT
  );

  readonly dirty = computed(
    () =>
      !formValuesEqual(this.formValues(), this._initialValues()) ||
      !otherCustomsEqual(this.otherCustoms(), this._initialCustoms())
  );

  readonly totalUiRows = FINANCIAL_SNAPSHOT_UI_ROW_COUNT;

  incomeSubs(): { key: string; label: string; formKey: string }[] {
    const tmpl = this.template[this.incomeKey];
    if (!tmpl?.subCategories) {
      return [];
    }
    return Object.entries(tmpl.subCategories)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([key, sub]) => ({
        key,
        label: sub.label,
        formKey: categorySubKey(this.incomeKey, key),
      }));
  }

  spendingSubs(catKey: string): { key: string; label: string; formKey: string }[] {
    const tmpl = this.template[catKey];
    if (!tmpl?.subCategories) {
      return [];
    }
    return Object.entries(tmpl.subCategories)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([key, sub]) => ({
        key,
        label: sub.label,
        formKey: categorySubKey(catKey, key),
      }));
  }

  categoryName(catKey: string): string {
    return this.template[catKey]?.name ?? catKey;
  }

  displayCategoryName(catKey: string): string {
    const name = this.categoryName(catKey);
    return name === 'Debt Repayment' ? 'Debt payments' : name;
  }

  /** Template `instructions` for a top-level category (tooltip copy). */
  categoryInstructions(catKey: string): string | null {
    const text = this.template[catKey]?.instructions?.trim();
    return text || null;
  }

  categorySum(catKey: string): number | null {
    return categorySumFromForm(catKey, this.formValues(), this.template, this.otherCustoms());
  }

  percentOfIncome(amount: number | null): number | null {
    const income = this.preview().metrics.householdIncome;
    if (amount === null || income === null || income <= 0) {
      return null;
    }
    return Math.round((amount / income) * 100);
  }

  otherNamedFormKey(sub: string): string {
    return otherSubKey(sub);
  }

  getNum(formKey: string): number | null {
    const v = this.formValues()[formKey];
    return typeof v === 'number' ? v : null;
  }

  setNum(formKey: string, value: number | null): void {
    this.formValues.update((m) => ({
      ...m,
      [formKey]: typeof value === 'number' && !Number.isNaN(value) ? value : undefined,
    }));
  }

  addOtherCustom(): void {
    this.otherCustoms.update((list) => [
      ...list,
      { key: newOtherCustomKey(), label: '', value: undefined },
    ]);
    this.expanded.update((m) => ({ ...m, [this.otherKey]: true }));
  }

  removeOtherCustom(key: string): void {
    this.otherCustoms.update((list) => list.filter((c) => c.key !== key));
  }

  setOtherCustomLabel(key: string, label: string): void {
    this.otherCustoms.update((list) =>
      list.map((c) => (c.key === key ? { ...c, label: label.slice(0, OTHER_CUSTOM_LABEL_MAX_LEN) } : c))
    );
  }

  setOtherCustomValue(key: string, value: number | null): void {
    this.otherCustoms.update((list) =>
      list.map((c) =>
        c.key === key
          ? {
              ...c,
              value: typeof value === 'number' && !Number.isNaN(value) ? value : undefined,
            }
          : c
      )
    );
  }

  getOtherCustomValue(key: string): number | null {
    const entry = this.otherCustoms().find((c) => c.key === key);
    return typeof entry?.value === 'number' ? entry.value : null;
  }

  /** Blur: trim / cap label for persistence readiness. */
  commitOtherCustomLabel(key: string): void {
    this.otherCustoms.update((list) =>
      list.map((c) => (c.key === key ? { ...c, label: sanitizeOtherCustomLabel(c.label) } : c))
    );
  }

  toggleExpanded(catKey: string): void {
    this.expanded.update((m) => ({ ...m, [catKey]: !m[catKey] }));
  }

  isExpanded(catKey: string): boolean {
    return !!this.expanded()[catKey];
  }

  tryClose(): void {
    if (!this.dirty()) {
      this._ref.close({ cancelled: true });
      return;
    }
    this._confirmation.confirm({
      message: 'You have unsaved changes — are you sure you want to discard them?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this._ref.close({ cancelled: true }),
    });
  }

  async saveProgress(): Promise<void> {
    await this._persist({ submit: false });
  }

  async submitSnapshot(): Promise<void> {
    if (!this.canSubmit()) {
      return;
    }
    await this._persist({ submit: true });
  }

  private async _persist(opts: { submit: boolean }): Promise<void> {
    this.saving.set(true);
    this.saveError.set(null);
    const succeededKeys: string[] = [];
    try {
      const user = await this._auth.getUser();
      const editorId = user?.sub ?? 'fa-unknown';
      const categories = stampFaAudit(
        buildCategoriesFromForm(this.formValues(), this.template, this.otherCustoms()),
        editorId
      );
      const fieldsEntered = countEnteredUiRows(
        { guid: this.guid, categories } satisfies FinancialSnapshotDocument,
        this.template
      );

      if (this.isSubmitted) {
        const previous = this._baselineSnapshot()?.categories ?? {};
        const changed = changedCategoryKeys(previous, categories);
        if (changed.length === 0) {
          this._messages.add({
            severity: 'info',
            summary: 'No changes',
            detail: 'Nothing to save.',
          });
          this._ref.close({ cancelled: false, saved: true });
          return;
        }
        for (const key of changed) {
          const body: Record<string, SnapshotCategoryData> = {
            [key]: categories[key] ?? { subCategories: {} },
          };
          await this.store.savePatch(this.guid, body);
          succeededKeys.push(key);
        }
      } else {
        await this.store.savePost({
          guid: this.guid,
          categories,
          createdDate: this._baselineSnapshot()?.createdDate ?? this._data.snapshot?.createdDate,
          submittedDate: opts.submit ? new Date().toISOString() : undefined,
          lastEditedBy: 'fa',
          lastEditedById: editorId,
        });
      }

      this._analytics.saved(this.guid, fieldsEntered);
      this._messages.add({
        severity: 'success',
        summary: opts.submit ? 'Snapshot submitted' : 'Progress saved',
        detail: opts.submit
          ? 'Financial snapshot submitted successfully.'
          : 'Financial snapshot progress saved.',
      });
      this._ref.close({ cancelled: false, saved: true, submitted: opts.submit });
    } catch (err) {
      if (this.isSubmitted) {
        await this._rebaseFromServerAfterPatchFailure(succeededKeys, err);
      } else {
        this.saveError.set(err instanceof Error ? err.message : 'Save failed');
      }
    } finally {
      this.saving.set(false);
    }
  }

  /**
   * After a mid-loop PATCH failure, reload server truth and rebase the diff baseline
   * so retry only sends remaining unsaved categories. Keeps current form edits.
   */
  private async _rebaseFromServerAfterPatchFailure(
    succeededKeys: string[],
    err: unknown
  ): Promise<void> {
    const failDetail = err instanceof Error ? err.message : 'Save failed';
    try {
      await this.store.reload();
    } catch {
      // Still surface the save failure even if reload fails.
    }

    const snap = this.store.snapshot();
    this._baselineSnapshot.set(snap);
    this._initialValues.set(initFormValues(snap, this.template));
    this._initialCustoms.set(initOtherCustoms(snap));

    const savedNames = succeededKeys.map((k) => this.displayCategoryName(k));
    if (savedNames.length === 0) {
      this.saveError.set(
        `${failDetail} Nothing was saved. We've refreshed from the server — review and try again.`
      );
      return;
    }
    this.saveError.set(
      `${failDetail} Saved: ${savedNames.join(', ')}. Remaining changes were not saved. We've refreshed from the server — review and try again.`
    );
  }
}
